// Single source of truth for every string on the FRONTECH agency marketing
// surface (AgencyHome, FrontProduct, FrontOSProduct). Edit here, not in
// components. Placeholders (WHATSAPP_NUMBER, CONTACT_EMAIL) are swapped in
// once the real values are known.

export const contact = {
  whatsappNumber: '92XXXXXXXXXX',
  whatsappHref: 'https://wa.me/92XXXXXXXXXX',
  // Routed to the same inbox via aliases. Listing them separately tells a
  // visitor which one to use, which gets the mail to the right place faster
  // than a single catch-all does.
  email: 'sales@frontech-consulting.com',
  emailHref: 'mailto:sales@frontech-consulting.com',
  inboxes: [
    { label: 'New work',  address: 'sales@frontech-consulting.com'   },
    { label: 'Support',   address: 'support@frontech-consulting.com' },
    { label: 'Invoices',  address: 'billing@frontech-consulting.com' },
  ],
  calLink: 'umer-khalid-oavynz/30min',
  calNamespace: '30min',
  whatsappSecondaryLabel: 'Prefer WhatsApp?',
};

// Company page for the brand, personal profile because that is where posts
// actually get reach — LinkedIn throttles company-page organic distribution.
// AgencyFooter drops any entry whose href still contains 'REPLACE'.
export const social = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/138684350/' },
  { label: 'X',        href: 'https://x.com/frontech_co' },
];

export const nav = {
  brand: 'FRONTECH',
  primaryCta: 'Book a call',
  products: [
    { label: 'Front',    to: '/front',    hint: 'AI support agent for your website' },
    { label: 'Front OS', to: '/front-os', hint: 'Desktop assistant (in development)' },
  ],
  links: [
    { label: 'Systems', to: '/#services' },
    { label: 'Start',   to: '/#start'    },
    { label: 'Method',  to: '/#process'  },
  ],
};

export const hero = {
  eyebrow: 'AI engineering',
  // Alternates kept for quick swaps:
  //   'AI that survives contact with your business.'
  //   'The engineering firm behind AI that actually runs.'
  //   'Built to run. Not to demo.'
  headline: 'We build the systems your business will run on.',
  subline:
    'Support that answers in seconds. Operations that run without supervision. A pipeline that fills itself. We build them, wire them into the tools you already use, and keep them running.',
  primary: 'Book a call',
  secondary: 'See what we run',
  secondaryHref: '#work',
  assessLinkText: 'Not sure what you need? Take the 5-minute assessment',
  assessCtaLabel: 'Find out what you need',
  assessHref: '/assess',
  // Engagement spans, rendered under the hero CTAs as measurements. The
  // figure that used to live here was cut: every module below shows a live
  // demo full-screen, so a diagram above them previewed nothing new.
  spans: [
    { label: 'Audit',  span: '0–2 days' },
    { label: 'Build',  span: '6–9 days' },
    { label: 'Run',    span: 'ongoing'  },
  ],
};

export const proofStrip = {
  label: 'Work delivered',
  items: [
    'Identity verification pipeline for a fintech',
    'Strategy engagement for a microfinance bank',
    'Two AI products built and shipped in-house',
  ],
};

export const problem = {
  headline: 'Most AI never makes it out of the demo.',
  body: [
    'The demo is the easy part. A working system has to reach your tools, survive the cases nobody scripted, stay accurate as the business changes, and still be right at 3am. That gap — between impressive and dependable — is the only thing we work on.',
  ],
  stat: {
    number: '86%',
    body:
      'of enterprise AI pilots never reach production. The cause is almost never the model. It is missing testing, missing permissions, and missing integration.',
    source:
      'Multiple 2026 studies (Gartner, McKinsey, MIT NANDA) — 77–95% depending on scope.',
  },
};

// ── Two ways in — the section that connects survey → audit → build ────────
export const start = {
  label: 'Start',
  headline: 'Two ways in. Neither one commits you to anything.',
  intro:
    'Most people arrive in one of two states. Pick the one that sounds like you.',
  paths: [
    {
      key: 'known',
      ref: 'PATH A',
      eyebrow: 'You already know what you want',
      title: 'Tell us what to build.',
      body:
        'You have the problem scoped and you want it built properly. We price it, agree a timeline, and start. No discovery theatre.',
      steps: [
        'A call to confirm scope and constraints',
        'Fixed price and timeline in writing',
        'Build starts',
      ],
      cta: 'Book a call',
      ctaKind: 'cal',
    },
    {
      key: 'unknown',
      ref: 'PATH B',
      eyebrow: 'You know something should change',
      title: 'We will show you where.',
      body:
        'Seven questions, five minutes, and you get a written read on where AI fits in your business and what it is worth. Free. You keep it whether or not we ever speak.',
      steps: [
        'Answer seven questions about how your business actually runs',
        'Get a ranked read on where the opportunity is',
        'If it looks real, the Audit goes deep on it',
      ],
      cta: 'Find out what you need',
      ctaKind: 'assess',
    },
  ],
  // The bridge between path B and paid work.
  bridge: {
    ref: 'THEN',
    title: 'The Audit',
    body:
      'One to two days, free. We sit with your team, watch how the work actually flows, and hand back a ranked map of every place AI genuinely fits — what each one is worth, what it would cost, how risky it is to build. You keep the plan whether or not you build with us. If nothing on it is worth building, that is what it will say.',
    outcome: 'Most engagements start here. Almost none of them should not.',
  },
};

export const rescue = {
  ref: 'ALT',
  headline: 'Already tried, and it never shipped?',
  body:
    'If you have an AI project that impressed everyone in the room and never reached a single customer, we will tell you why in two days — and what it would honestly cost to finish. Then you get a clean choice: kill it, finish it in-house, or finish it with us. The model is almost never the problem.',
  cta: 'Talk to us about a stalled project',
};

export const process = {
  label: 'Method',
  headline: 'Build it. Wire it in. Then keep it alive.',
  intro:
    'The third step is the one that separates a system from a demo, and it is the one almost nobody sells you.',
  steps: [
    {
      n: '01',
      title: 'Audit',
      span: '0–2 days',
      body:
        'A ranked map of where AI genuinely fits, what each opportunity is worth, and what it would cost to build. Free, after a call that says the fit is real. You keep the plan whether or not you build with us.',
    },
    {
      n: '02',
      title: 'Build',
      span: '6–9 days',
      body:
        'We build the system, connect it to the tools you already use, and test it against real cases pulled from your own business — before it ever reaches a customer.',
    },
    {
      n: '03',
      title: 'Run',
      span: 'ongoing',
      body:
        'We monitor it, fix it when it drifts, and improve it as the business changes. AI systems drift faster than most software. Someone has to be watching.',
    },
  ],
};

export const statusBoard = {
  label: 'Running',
  headline: 'We run our own software in production.',
  body:
    'Not reselling somebody else’s platform. The support agent we deploy is software we built, ship, and keep running ourselves.',
  rows: [
    {
      name: 'Front',
      version: 'v0.5',
      status: 'PRODUCTION',
      state: 'live',
      description:
        'AI support agent that installs on any website. Reads your site, learns your business, answers in text or voice.',
      href: '/front',
      linkLabel: 'About Front',
    },
    {
      name: 'Front OS',
      version: '—',
      status: 'IN DEVELOPMENT',
      state: 'dev',
      description:
        'Desktop assistant that sees what you are working on and remembers your tasks. For people who live in ten applications at once.',
      href: '/front-os',
      linkLabel: 'Join the waitlist',
    },
  ],
};

export const closingCta = {
  headline: 'The next quarter runs either way.',
  body:
    'The only question is how much of it your team spends doing work a system should have absorbed. Start with a call, or find out what you need in five minutes.',
  cta: 'Book a call',
  assessLinkText: 'Not sure yet? Take the 5-minute assessment first',
  assessCtaLabel: 'Find out what you need',
  assessHref: '/assess',
};

export const footer = {
  tagline: 'AI engineering',
  productLinks: [
    { label: 'Front',    to: '/front' },
    { label: 'Front OS', to: '/front-os' },
  ],
  legalLinks: [
    { label: 'Privacy',  to: '/privacy' },
    { label: 'Terms',    to: '/terms' },
    { label: 'Refunds',  to: '/refunds' },
  ],
};

export const frontOs = {
  eyebrow: 'Front OS · In development',
  headline: 'A desktop assistant that remembers what you’re working on.',
  subline:
    'Built for people who live in ten applications at once. Sees what is on your screen, remembers your tasks, and helps you pick up where you left off — every time you switch context.',
  waitlist: {
    headline: 'Join the waitlist.',
    body: 'We’ll email you once when Front OS is ready to try. No newsletter, no marketing.',
    placeholder: 'you@company.com',
    cta: 'Join waitlist',
    success: 'You’re on the list. We’ll be in touch.',
    error: 'Something went wrong. Please try again or email us directly.',
  },
  credibility: {
    label: 'Why us',
    body:
      'Front OS is being built by the team that runs Front — an AI support agent already in production. Same discipline: real memory, permission-scoped tool use, and an operator you can trust with your desktop.',
  },
};
